// TableColumnFilters.js
import React, { useEffect, useMemo, useState } from "react";
import "./TableColumnFilters.css";
import DynamicButton from "../DynamicButton/DynamicButton";

const TIME_MINUTES = ["00", "15", "30", "45"];

const isFilterOn = (v) =>
  v === true ||
  String(v).toLowerCase() === "true" ||
  v === 1 ||
  String(v) === "1";

// --- time helpers ---
function parseTime12(s) {
  const m = String(s || "").match(/^\s*(\d{1,2}):(\d{2})\s*(AM|PM)\s*$/i);
  if (!m) return { h: "", m: "", p: "" };
  return { h: String(Number(m[1])), m: m[2], p: m[3].toUpperCase() };
}
function fmtTime12(h, m, p) {
  if (!h || !m || !p) return "";
  const hh = String(((Number(h) - 1 + 12) % 12) + 1); // 1..12
  const mm = String(m).padStart(2, "0");
  const pp = String(p).toUpperCase() === "PM" ? "PM" : "AM";
  return `${hh}:${mm} ${pp}`;
}

// --- date helper ---
// Accepts "YYYY-MM-DD" or any ISO string and returns "YYYY-MM-DD"
const toYYYYMMDD = (v) => {
  if (!v) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  const m = String(v).match(/^(\d{4}-\d{2}-\d{2})/);
  if (m) return m[1];
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  const yr = d.getUTCFullYear();
  const mo = String(d.getUTCMonth() + 1).padStart(2, "0");
  const da = String(d.getUTCDate()).padStart(2, "0");
  return `${yr}-${mo}-${da}`;
};

export default function TableColumnFilters({
  columns = [],
  columnFilters,
  setColumnFilters,
  setCurrentPage,
}) {
  const [open, setOpen] = useState(false);
  const [fieldSearch, setFieldSearch] = useState("");

  const [optionsCache, setOptionsCache] = useState({});
  const [loadingField, setLoadingField] = useState({});
  const [errorField, setErrorField] = useState({});

  const handleSet = (field, value) => {
    setColumnFilters((prev) => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  };

  // Optional normalizer (kept for time)
  const handleSetNormalized = (col, raw) => {
    const type = (col.type || "").toLowerCase();
    if (type === "time") {
      const { h, m, p } = parseTime12(raw);
      const val = h && m && p ? fmtTime12(h, m, p) : raw;
      return handleSet(col.field, val);
    }
    return handleSet(col.field, raw);
  };

  const handleClearOne = (field) => {
    setColumnFilters((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
    setCurrentPage(1);
  };

  const clearAll = () => {
    setColumnFilters({});
    setCurrentPage(1);
  };

  // Only fields with filter=true
  const filterableColumns = useMemo(
    () => (columns || []).filter((c) => isFilterOn(c.filter)),
    [columns]
  );

  // Prefetch API options for filterable API-backed dropdowns
  useEffect(() => {
    const toLoad = (filterableColumns || []).filter(
      (c) =>
        c.api && c.apiTitle && !optionsCache[c.field] && !loadingField[c.field]
    );
    if (!toLoad.length) return;

    toLoad.forEach((col) => {
      const { field, api, apiTitle } = col;
      setLoadingField((m) => ({ ...m, [field]: true }));
      setErrorField((m) => ({ ...m, [field]: "" }));

      fetch(api)
        .then(async (res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();
          const arr = Array.isArray(data)
            ? data
            : Array.isArray(data?.data)
            ? data.data
            : [];
          const opts = arr
            .map((it) => {
              const label = it?.[apiTitle];
              const value = it?._id ?? label ?? "";
              return label
                ? { label: String(label), value: String(value) }
                : null;
            })
            .filter(Boolean);
          setOptionsCache((m) => ({ ...m, [field]: opts }));
        })
        .catch((e) => {
          setErrorField((m) => ({
            ...m,
            [field]: e.message || "Failed to load options",
          }));
        })
        .finally(() => {
          setLoadingField((m) => ({ ...m, [field]: false }));
        });
    });
  }, [filterableColumns, optionsCache, loadingField]);

  // Field search (among filterable only)
  const visibleColumns = useMemo(() => {
    const q = fieldSearch.trim().toLowerCase();
    if (!q) return filterableColumns;
    return (filterableColumns || []).filter((c) =>
      String(c.label || c.field || "")
        .toLowerCase()
        .includes(q)
    );
  }, [filterableColumns, fieldSearch]);

  // Consider date ranges active if either bound is present
  const isActiveField = (col) => {
    const type = (col.type || "").toLowerCase();
    const field = col.field;
    if (type === "date") {
      return !!(
        columnFilters[`${field}__from`] || columnFilters[`${field}__to`]
      );
    }
    return !!columnFilters[field];
  };

  const renderFilterControl = (col) => {
    const field = col.field;
    const type = (col.type || "").toLowerCase();
    const value = columnFilters[field] ?? "";

    if (type === "boolean") {
      return (
        <select
          className="filterRight-input"
          value={value}
          onChange={(e) => handleSet(field, e.target.value)}
        >
          <option value="">All</option>
          <option value="true">True</option>
          <option value="false">False</option>
        </select>
      );
    }

    // DATE -> From / To range
    if (type === "date") {
      const fromKey = `${field}__from`;
      const toKey = `${field}__to`;
      const fromVal = toYYYYMMDD(columnFilters[fromKey] || "");
      const toVal = toYYYYMMDD(columnFilters[toKey] || "");

      return (
        <div style={{ display: "grid", gap: 8 }}>
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ flex: 1 }}>
              <label className="filterRight-label" style={{ fontWeight: 500 }}>
                From
              </label>
              <input
                type="date"
                className="filterRight-input"
                value={fromVal}
                onChange={(e) => handleSet(fromKey, toYYYYMMDD(e.target.value))}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label className="filterRight-label" style={{ fontWeight: 500 }}>
                To
              </label>
              <input
                type="date"
                className="filterRight-input"
                value={toVal}
                onChange={(e) => handleSet(toKey, toYYYYMMDD(e.target.value))}
              />
            </div>
          </div>

          {(fromVal || toVal) && (
            <DynamicButton
              text="Clear range"
              icon="fa-eraser"
              size="sm"
              variant="secondary"
              onClick={() => {
                handleClearOne(fromKey);
                handleClearOne(toKey);
              }}
            />
          )}
        </div>
      );
    }

    // TIME (kept as exact match control)
    if (type === "time") {
      const { h, m, p } = parseTime12(value);
      return (
        <div style={{ display: "flex", gap: 6 }}>
          <select
            className="filterRight-input"
            value={h}
            onChange={(e) =>
              handleSetNormalized(
                col,
                fmtTime12(e.target.value, m || "00", p || "AM")
              )
            }
          >
            <option value="">HH</option>
            {[...Array(12)].map((_, i) => {
              const hh = String(i + 1);
              return (
                <option key={hh} value={hh}>
                  {hh}
                </option>
              );
            })}
          </select>
          <select
            className="filterRight-input"
            value={m}
            onChange={(e) =>
              handleSetNormalized(
                col,
                fmtTime12(h || "6", e.target.value, p || "AM")
              )
            }
          >
            <option value="">MM</option>
            {TIME_MINUTES.map((mm) => (
              <option key={mm} value={mm}>
                {mm}
              </option>
            ))}
          </select>
          <select
            className="filterRight-input"
            value={p}
            onChange={(e) =>
              handleSetNormalized(
                col,
                fmtTime12(h || "6", m || "00", e.target.value)
              )
            }
          >
            <option value="">AM/PM</option>
            <option value="AM">AM</option>
            <option value="PM">PM</option>
          </select>
        </div>
      );
    }

    // Static dropdown (e.g. Status)
    if (type === "dropdown" && Array.isArray(col.options) && !col.api) {
      return (
        <select
          className="filterRight-input"
          value={value}
          onChange={(e) => handleSet(field, e.target.value)}
        >
          <option value="">All</option>
          {col.options.map((opt, i) => {
            const v = typeof opt === "string" ? opt : String(opt);
            return (
              <option key={i} value={v}>
                {v}
              </option>
            );
          })}
        </select>
      );
    }

    // API-backed dropdowns
    if (type === "dropdown" && col.api && col.apiTitle) {
      const opts = optionsCache[field] || [];
      const loading = !!loadingField[field];
      const error = errorField[field];

      return (
        <>
          <select
            className="filterRight-input"
            value={value}
            onChange={(e) => handleSet(field, e.target.value)}
            disabled={loading || !!error}
          >
            <option value="">{loading ? "Loading..." : "All"}</option>
            {!loading &&
              !error &&
              opts.map((opt) => (
                <option key={opt.value} value={opt.label}>
                  {opt.label}
                </option>
              ))}
          </select>
          {error && (
            <div style={{ color: "#b91c1c", fontSize: 12, marginTop: 4 }}>
              {error}
            </div>
          )}
        </>
      );
    }

    // API-backed multi (e.g., Hazards)
    if (
      type === "checkbox" &&
      col.array === "true" &&
      col.api &&
      col.apiTitle
    ) {
      const opts = optionsCache[field] || [];
      const loading = !!loadingField[field];
      const error = errorField[field];

      const selected = String(value)
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

      const update = (vals) => handleSet(field, vals.join(", "));

      return (
        <>
          <select
            multiple
            className="filterRight-input"
            value={selected}
            onChange={(e) => {
              const next = Array.from(e.target.selectedOptions).map(
                (o) => o.value
              );
              update(next);
            }}
            size={Math.min(8, Math.max(4, (opts || []).length))}
            style={{ minHeight: 110 }}
            disabled={loading || !!error}
          >
            {loading && <option>Loading…</option>}
            {!loading &&
              !error &&
              opts.map((opt) => (
                <option key={opt.value} value={opt.label}>
                  {opt.label}
                </option>
              ))}
          </select>
          {error && (
            <div style={{ color: "#b91c1c", fontSize: 12, marginTop: 4 }}>
              {error}
            </div>
          )}
        </>
      );
    }

    // Default text filter
    return (
      <input
        type="text"
        className="filterRight-input"
        placeholder={`Filter ${col.label || col.field}`}
        value={value}
        onChange={(e) => handleSet(field, e.target.value)}
      />
    );
  };

  return (
    <>
      <DynamicButton
        text="Filter Columns"
        icon="fa-filter"
        size="sm"
        variant="outline"
        onClick={() => setOpen(true)}
      />

      <div
        className={`filterRight-slider ${open ? "open" : ""}`}
        role="dialog"
        aria-modal="true"
      >
        <div className="filterRight-slider-header">
          <h3>Column Filters</h3>
        </div>

        <div className="filterRight-slider-content">
          {/* Search field names (among filterable only) */}
          <div className="filterRight-group">
            <label className="filterRight-label">Find a field</label>
            <input
              type="text"
              className="filterRight-input"
              placeholder="Search fields…"
              value={fieldSearch}
              onChange={(e) => setFieldSearch(e.target.value)}
            />
          </div>

          {(visibleColumns || []).map((col, idx) => {
            const type = (col.type || "").toLowerCase();
            const field = col.field;
            const fromKey = `${field}__from`;
            const toKey = `${field}__to`;

            return (
              <div key={idx} className="filterRight-group">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                  }}
                >
                  <label className="filterRight-label">
                    {col.label || col.field}
                  </label>

                  {isActiveField(col) ? (
                    <DynamicButton
                      text="Clear"
                      icon="fa-times"
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        if (type === "date") {
                          handleClearOne(fromKey);
                          handleClearOne(toKey);
                        } else {
                          handleClearOne(field);
                        }
                      }}
                      title="Clear this filter"
                    />
                  ) : null}
                </div>

                {renderFilterControl(col)}
              </div>
            );
          })}
        </div>

        <div
          className="filterRight-actions"
          style={{ display: "flex", gap: 8 }}
        >
          <DynamicButton
            text="Clear All"
            icon="fa-broom"
            size="sm"
            variant="danger"
            onClick={clearAll}
          />
          <DynamicButton
            text="Close"
            icon="fa-times"
            size="sm"
            variant="secondary"
            onClick={() => setOpen(false)}
          />
        </div>
      </div>
    </>
  );
}
